import { Paths } from 'services/navigation/paths';

import { Navigate, Route, Routes } from 'react-router-dom';

import { PrivateRoute } from 'services/navigation/components/PrivateRoute';
import { PublicRoute } from 'services/navigation/components/PublicRoute';
import { RedirectRoute } from 'services/navigation/components/RedirectRoute';
import { useNavigationHandler } from 'services/navigation/hooks/useNavigationHandler';

import { LoginPage } from 'pages/Login/LoginPage';
import { LogoutPage } from 'pages/Logout/LogoutPage';
import { InvitePage } from 'pages/Invite/InvitePage';
import { Profile } from 'pages/Profile/ProfilePage';
import { Campaigns } from 'pages/Campaigns/CampaignsPage';
import { CampaignOpeningMessages } from 'pages/Campaigns/subpages/CampaignOpeningMessages';
import { CampaignPage } from 'pages/Campaigns/subpages/CampaignPage';
import { HomeTeamPageTab } from 'pages/Home/HomeTeamPageTab';
import { HomeDocumentsTab } from 'pages/Home/HomeDocumentsTab';
import { CandidateRankings } from 'pages/CandidatesRanking/CandidateRakingsPage';
import { ResetPasswordPage } from 'pages/ResetPassword/ResetPasswordPage';
import ContactSupport from 'pages/ContactSupport/ContactSupport';
import { CampaignAutomationPage } from 'pages/CampaignAutomationPage';
import { CandidateRankingGroupPage } from 'pages/CandidateRankingGroupPage';
import { CandidateRankingGroupCreationPage } from 'pages/CandidateRankingGroupCreationPage';
import { Administrator } from './pages/AdministratorPage/AdministratorPage';
import { AdministratorContractDashboardTab } from './pages/AdministratorPage/AdministratorContractDashboardTab';
import { ChannelsPage } from 'pages/ChannelsPage/ChannelsPage';
import { CalendarAddContactPage } from 'pages/CalendarMeetings/CalendarAddContactPage';
import { CalendarCreateMeetingTemplatePage } from 'pages/CalendarMeetings/CalendarCreateMeetingTemplatePage';
import { ConnectionSuccessfulPage } from 'pages/Public/pages/calendar/ConnectionSuccessfulPage';
import { ConnectionFailedPage } from 'pages/Public/pages/calendar/ConnectionFailedPage';
import { CalendarEditMeetingTemplatePage } from 'pages/CalendarMeetings/CalendarEditMeetingTemplatePage';
import { EmailOptOutPage } from 'pages/Public/pages/opt-out/EmailOptOutOutPage';
import { AnalyseDetailsPage } from 'pages/Analyse/AnalyseDetailsPage';
import { CampaignCreationWithParticipants } from 'pages/CampaignCreationWithParticipants/CampaignCreationWithParticipants';
import { SelectExistingCampaign } from 'pages/CampaignCreationWithParticipants/subpages/SelectExistingCampaign';
import { CampaignCreationWithParticipantsSummary } from 'pages/CampaignCreationWithParticipants/subpages/CampaignCreationWithParticipantsSummary';
import { AnalyseTemplatesPage } from 'pages/AnalyseTemplates/AnalyseTemplatesPage';
import { AnalyseRulesetsPage } from 'pages/AnalyseRulesets/AnalyseRulesetsPage';
import { AnalyseTemplateCreationPage } from 'pages/AnalyseTemplateCreation/AnalyseTemplateCreationPage';
import { AnalyseTemplateEditPage } from 'pages/AnalyseTemplateEdit/AnalyseTemplateEditPage';
import { HomeUsagePageTab } from 'pages/Home/HomeUsagePageTab';
import { HomeDashboardTab } from 'pages/Home/HomeDashboardTab';
import { HomeChannelsTab } from 'pages/Home/HomeChannelsTab';
import { HomeCalendarMeetingsTab } from 'pages/Home/HomeCalendarMeetingsTab';
import { AnalyseCreation } from 'pages/Analyse/AnalyseCreation';
import { NoATSAnalysisAddCV } from 'pages/Analyse/NoATSAnalysisAddCV';
import { AnalyseRulesetAddATSJobs } from 'pages/AnalyseRulesets/AnalyseRulesetAddATSJobs';
import { CampaignCreationV3 } from 'pages/CampaignCreation/CampaignCreationV3';
import { CampaignAddMoreCSVCandidates } from 'pages/CampaignCreation/CampaignAddMoreCSVCandidates';
import { navigationRoutesV2 } from 'core/services/navigation/navigationRoutesV2';
import { PathsV2 } from 'core/services/navigation/pathsV2';

export const AppNavigator = () => {
  useNavigationHandler();

  return (
    <Routes>
      {Object.entries(navigationRoutesV2).map(
        ([path, { component, type, disablePadding }]) => (
          <Route
            key={path}
            path={PathsV2[path as keyof typeof PathsV2]}
            element={
              <PrivateRoute
                element={component}
                useLayoutContainer
                isAnalyse={type === 'analyse'}
                isCampaign={type === 'campaign'}
                isInternalSourcing={type === 'internal-sourcing'}
                disablePadding={disablePadding}
              />
            }
          />
        )
      )}
      {/* PUBLIC */}
      <Route
        path={Paths.LOGIN}
        element={<PublicRoute element={<LoginPage />} />}
      />
      {/* Bare route (no Public/PrivateRoute wrapper) so logout runs for both
          authenticated and unauthenticated visitors. */}
      <Route path={Paths.LOGOUT} element={<LogoutPage />} />
      <Route
        path={Paths.CALENDAR_CONNECTION_SUCCESSFUL}
        element={<PublicRoute element={<ConnectionSuccessfulPage />} />}
      />
      <Route
        path={Paths.CALENDAR_CONNECTION_FAILURE}
        element={<PublicRoute element={<ConnectionFailedPage />} />}
      />
      <Route
        path={Paths.EMAIL_OPT_OUT_SUCCESS}
        element={
          <PublicRoute element={<EmailOptOutPage redirectType="success" />} />
        }
      />
      <Route
        path={Paths.EMAIL_OPT_OUT_FAILURE}
        element={
          <PublicRoute element={<EmailOptOutPage redirectType="failed" />} />
        }
      />
      {/* HOME */}
      <Route
        path={Paths.HOME}
        element={<PrivateRoute element={<HomeDashboardTab />} />}
      />
      <Route
        path={Paths.HOME_TEAM}
        element={<PrivateRoute element={<HomeTeamPageTab />} />}
      />
      <Route
        path={Paths.HOME_CALENDAR_MEETINGS}
        element={<PrivateRoute element={<HomeCalendarMeetingsTab />} />}
      />
      <Route
        path={Paths.HOME_CHANNELS}
        element={<PrivateRoute element={<HomeChannelsTab />} />}
      />
      <Route
        path={Paths.HOME_DOCUMENTS}
        element={<PrivateRoute element={<HomeDocumentsTab />} />}
      />
      <Route
        path={Paths.HOME_USAGE}
        element={<PrivateRoute element={<HomeUsagePageTab />} />}
      />

      {/* CALENDAR */}
      <Route
        path={Paths.CALENDAR_MEETING_TEMPLATE_CREATE}
        element={
          <PrivateRoute
            useLayoutContainer
            element={<CalendarCreateMeetingTemplatePage />}
          />
        }
      />
      <Route
        path={`${Paths.CALENDAR_MEETING_TEMPLATE_EDIT}/:id`}
        element={
          <PrivateRoute
            useLayoutContainer
            element={<CalendarEditMeetingTemplatePage />}
          />
        }
      />
      <Route
        path={`${Paths.CALENDAR_MEETING_TEMPLATE_PREVIEW}/:id`}
        element={
          <PrivateRoute
            useLayoutContainer
            element={<CalendarEditMeetingTemplatePage isPreview />}
          />
        }
      />
      <Route
        path={Paths.CALENDAR_ADD_CONTACT}
        element={<PrivateRoute element={<CalendarAddContactPage />} />}
      />
      {/* SETTINGS */}
      <Route
        path={Paths.ONBOARDING}
        element={<PrivateRoute element={<Profile />} />}
      />
      <Route
        path={Paths.PROFILE}
        element={<PrivateRoute element={<Profile />} />}
      />
      <Route
        path={Paths.ADMINISTRATOR}
        element={
          <PrivateRoute element={<AdministratorContractDashboardTab />} />
        }
      />
      <Route
        path={Paths.ADMINISTRATOR_ACCOUNTS_OVERVIEW}
        element={<PrivateRoute element={<Administrator />} />}
      />
      <Route
        path={Paths.RESET_PASSWORD}
        element={<PublicRoute element={<ResetPasswordPage />} />}
      />
      <Route
        path={Paths.INVITE}
        element={<PublicRoute element={<InvitePage />} />}
      />
      {/* Campaigns */}
      <Route
        path={Paths.CAMPAIGNS}
        element={<PrivateRoute isCampaign element={<Campaigns />} />}
      />
      <Route
        path={`${Paths.OUTBOUND_CAMPAIGN}/:id`}
        element={<PrivateRoute isCampaign element={<CampaignPage />} />}
      />
      <Route
        path={`${Paths.CAMPAIGN_CREATE_DRAFT}/:id?`}
        element={
          <PrivateRoute
            isCampaign
            useLayoutContainer
            element={<CampaignCreationV3 />}
          />
        }
      />
      <Route
        path={`${Paths.CAMPAIGN_OUTBOUND_OPENING_MESSAGES}/:id`}
        element={
          <PrivateRoute
            isCampaign
            useLayoutContainer
            element={<CampaignOpeningMessages />}
          />
        }
      />
      <Route
        path={`${Paths.CAMPAIGN_ADD_CSV_CANDIDATES}/:id`}
        element={
          <PrivateRoute
            isCampaign
            useLayoutContainer
            element={<CampaignAddMoreCSVCandidates />}
          />
        }
      />
      <Route
        path={`${Paths.CAMPAIGN_AUTOMATION}/:campaignId`}
        element={
          <PrivateRoute isCampaign element={<CampaignAutomationPage />} />
        }
      />
      <Route
        path={Paths.CAMPAIGN_CREATE_WITH_PARTICIPANTS}
        element={
          <PrivateRoute
            isCampaign
            element={<CampaignCreationWithParticipants />}
          />
        }
      />
      <Route
        path={Paths.CAMPAIGN_CREATE_WITH_PARTICIPANTS_REVIEW}
        element={
          <PrivateRoute
            isCampaign
            element={<CampaignCreationWithParticipantsSummary />}
          />
        }
      />
      <Route
        path={Paths.CAMPAIGN_CREATE_WITH_PARTICIPANTS_SELECT_CAMPAIGN}
        element={
          <PrivateRoute isCampaign element={<SelectExistingCampaign />} />
        }
      />
      {/* Templates */}
      <Route
        path={Paths.ANALYSE_TEMPLATES}
        element={<PrivateRoute isAnalyse element={<AnalyseTemplatesPage />} />}
      />
      <Route
        path={Paths.ANALYSE_RULESETS}
        element={<PrivateRoute isAnalyse element={<AnalyseRulesetsPage />} />}
      />
      <Route
        path={Paths.ANALYSE_TEMPLATE_CREATE}
        element={
          <PrivateRoute isAnalyse element={<AnalyseTemplateCreationPage />} />
        }
      />
      <Route
        path={`${Paths.ANALYSE_TEMPLATES}/:id`}
        element={
          <PrivateRoute isAnalyse element={<AnalyseTemplateEditPage />} />
        }
      />
      {/* Analyses */}
      <Route
        path={Paths.CANDIDATES_RANKING}
        element={<PrivateRoute isAnalyse element={<CandidateRankings />} />}
      />
      <Route
        path={`${Paths.ANALYSE}/:id`}
        element={
          <PrivateRoute
            isAnalyse
            useLayoutContainer
            element={<AnalyseDetailsPage />}
          />
        }
      />
      <Route
        path={`${Paths.ANALYSE_CREATE}/:id?`}
        element={<PrivateRoute isAnalyse element={<AnalyseCreation />} />}
      />
      <Route
        path={Paths.ANALYSE_ADD_CV}
        element={<PrivateRoute isAnalyse element={<NoATSAnalysisAddCV />} />}
      />
      <Route
        path={`${Paths.ANALYSE_RULESETS}/:id`}
        element={
          <PrivateRoute isAnalyse element={<CandidateRankingGroupPage />} />
        }
      />
      <Route
        path={`${Paths.ANALYSE_RULESET_CREATE}`}
        element={
          <PrivateRoute
            isAnalyse
            element={<CandidateRankingGroupCreationPage />}
          />
        }
      />
      <Route
        path={Paths.ANALYSE_RULESET_ADD_JOBS}
        element={
          <PrivateRoute isAnalyse element={<AnalyseRulesetAddATSJobs />} />
        }
      />
      {/* MISC */}
      <Route
        path={Paths.CONTACT_SUPPORT}
        element={<PrivateRoute element={<ContactSupport />} />}
      />
      <Route
        path={Paths.CHANNELS}
        element={<PrivateRoute element={<ChannelsPage />} />}
      />
      {/* Redirects for deprecated paths */}
      <Route
        path={`${Paths.CANDIDATES_RANKING_GROUP}/:id`}
        element={<RedirectRoute to={`${Paths.ANALYSE_RULESETS}/:id`} />}
      />
      <Route
        path={Paths.CANDIDATES_RANKING_GROUP_CREATE}
        element={<RedirectRoute to={Paths.ANALYSE_RULESET_CREATE} />}
      />
      {/* Catch-all route */}
      <Route path="*" element={<Navigate to={Paths.LOGIN} />} />
    </Routes>
  );
};
